import { useAsyncData, useState } from '#imports'
import { computed, onMounted, watch } from 'vue'
import type { LocationQuery } from 'vue-router'

import type { BuybackOffer } from '@backmarket/http-api/src/api-models/BuybackOffer'
import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { calculateReduction } from '@backmarket/utils/math/calculateReduction'

import { useBuybackEnabled } from '../../../../composables/useBuybackEnabled/useBuybackEnabled'
import { SWAP_INFO_MESSAGE } from '../../../../constants'
import {
  buybackOfferToLegacyOffer,
  legacyOfferToBuybackOffer,
  offerV1ToBuybackOffer,
} from '../../adapters/buybackOffer/buybackOffer'
import type { GetOfferV1Response } from '../../api/types'

export function useBuybackOffer() {
  const { market } = useMarketplace()
  const { isBuybackEnabled } = useBuybackEnabled()

  const $httpFetch = useHttpFetch()
  const i18n = useI18n()
  const logger = useLogger()

  const defaultBuybackOfferPrice: Price = {
    amount: '140',
    currency: market.defaultCurrency,
  }

  const freePrice: Price = {
    amount: '0',
    currency: market.defaultCurrency,
  }

  const fetchedFromCart = useState<boolean>(
    'buyback-offer-from-cart',
    () => false,
  )

  const buybackOffer = useState<GetOfferV1Response | null>(
    'buyback-offer',
    () => null,
  )
  const buybackOfferPayload = useState<Record<string, unknown> | null>(
    'buyback-offer-payload',
    () => null,
  )
  const hasNoOffer = useState<boolean>('buyback-has-no-offer', () => false)

  const cartPriceWithSwapDiscount = useState<number | null>(
    'buyback-cart-price-with-swap-discount',
    () => null,
  )

  const { data: swapOfferFromCart, execute } = useAsyncData(
    async () => {
      return $httpFetch('/bm/cart/swap')
    },
    { immediate: false },
  )

  const hasBuybackOffer = computed(() => buybackOffer.value !== null)

  const hasBuybackOfferAvailable = computed(
    () => buybackOffer.value !== null || hasNoOffer.value,
  )

  const legacyOffer = computed<BuybackOffer | null>(() => {
    if (!buybackOffer.value) return null

    return offerV1ToBuybackOffer(
      buybackOffer.value,
      buybackOfferPayload.value,
      i18n.currencySign,
    )
  })

  const cartSwapAdPrice = computed<Price | undefined>(() => {
    if (cartPriceWithSwapDiscount.value === null) return undefined

    return {
      amount: cartPriceWithSwapDiscount.value.toString(),
      currency: market.defaultCurrency,
    }
  })

  function setCartPriceWithSwapDiscount(value: number | null) {
    cartPriceWithSwapDiscount.value = value
  }

  function setBuybackOffer(
    offer: GetOfferV1Response,
    payload: LocationQuery | Record<string, unknown>,
  ) {
    buybackOffer.value = offer
    buybackOfferPayload.value = payload
  }

  function resetBuybackOffer() {
    buybackOffer.value = null
    hasNoOffer.value = false
  }

  async function deleteBuybackOffer() {
    try {
      await $httpFetch('/bm/cart/swap', { method: 'DELETE' })

      resetBuybackOffer()
    } catch (err) {
      const deletedError = err as HttpApiError
      logger.error(SWAP_INFO_MESSAGE.DELETE_SWAP, {
        error: deletedError,
        owners: ['bot-squad-circularity-offer-front'],
      })
    }
  }

  function resetHasNoOffer() {
    hasNoOffer.value = false
  }

  function getDiscountedPrice(initialPrice: Price | null) {
    if (!initialPrice) {
      return freePrice
    }
    const finalPrice = calculateReduction(
      initialPrice,
      buybackOffer.value?.listing.price ?? defaultBuybackOfferPrice,
    )

    return finalPrice && parseFloat(finalPrice.amount) >= 0
      ? finalPrice
      : freePrice
  }

  async function saveBuybackOffer(
    offer: GetOfferV1Response,
    payload: LocationQuery | Record<string, unknown>,
  ) {
    await $httpFetch('/bm/cart/swap', {
      method: 'POST',
      body: buybackOfferToLegacyOffer(offer, payload, i18n.currencySign),
    })

    setBuybackOffer(offer, payload)
  }

  function saveHasNoOffer() {
    hasNoOffer.value = true
  }

  function getDiscountedPriceForProductCard(initialPrice: Price) {
    if (initialPrice && buybackOffer.value?.listing.price) {
      const finalPrice = calculateReduction(
        initialPrice,
        buybackOffer.value?.listing.price,
      )

      return finalPrice && parseFloat(finalPrice.amount) >= 0
        ? finalPrice
        : freePrice
    }

    return undefined
  }

  function logSwapDesync(offerPriceAmount: string | null) {
    if (
      !fetchedFromCart.value ||
      (offerPriceAmount === null && buybackOffer.value === null)
    ) {
      return
    }

    if (
      (offerPriceAmount === null && buybackOffer.value !== null) ||
      (offerPriceAmount !== null && buybackOffer.value === null)
    ) {
      logger.info('[BUYBACK][CART][SWAP] Offer desync: offer deleted', {
        owners: ['bot-squad-circularity-offer-front'],
      })

      return
    }
    if (
      offerPriceAmount &&
      buybackOffer.value &&
      offerPriceAmount !== buybackOffer.value?.listing.price.amount
    ) {
      logger.info(
        '[BUYBACK][CART][SWAP] Offer desync: offer prices are different',
        {
          prices: {
            cart: offerPriceAmount,
            offer: buybackOffer.value?.listing.price.amount,
          },
          owners: ['bot-squad-circularity-offer-front'],
        },
      )
    }
  }

  watch(swapOfferFromCart, (newSwapOfferFromCart) => {
    if (newSwapOfferFromCart && !buybackOffer.value) {
      setBuybackOffer(
        legacyOfferToBuybackOffer(newSwapOfferFromCart),
        newSwapOfferFromCart.diagnostic_payload,
      )
    }
  })

  onMounted(() => {
    if (!fetchedFromCart.value && isBuybackEnabled) {
      void execute()
      fetchedFromCart.value = true
    }
  })

  return {
    buybackOffer,
    buybackOfferPayload,
    hasBuybackOffer,
    hasBuybackOfferAvailable,
    legacyOffer,
    cartSwapAdPrice,
    setCartPriceWithSwapDiscount,
    logSwapDesync,
    saveBuybackOffer,
    saveHasNoOffer,
    getDiscountedPrice,
    getDiscountedPriceForProductCard,
    deleteBuybackOffer,
    resetBuybackOffer,
    resetHasNoOffer,
  }
}
